import {
  collection,
  doc,
  orderBy,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/core/firebase/client'
import { FirestoreRepository } from '@/core/firebase/repository'
import { clubSubcollections } from '@/core/firebase/paths'
import { StaffMemberSchema, type StaffMember } from '@/core/models/staff'
import { normalizeName, normalizeEmail } from '@/shared/utils/input-normalization'

/**
 * Thrown when `updateStaff` detects the staff member's role or active flag
 * was already changed by someone else. Callers should surface "otro admin
 * acaba de cambiar este miembro — recarga y vuelve a intentarlo".
 */
export class StaleStaffError extends Error {
  constructor(expected: unknown, actual: unknown) {
    super(`Staff changed concurrently: expected ${String(expected)} got ${String(actual)}`)
    this.name = 'StaleStaffError'
  }
}

/**
 * Thrown by `inviteStaff` when the email is already used by a staff doc
 * anywhere in the platform — including this same club, another club, or a
 * deactivated record. Callers should surface a "este email ya pertenece a
 * otro miembro del personal — contacta con soporte" alert.
 *
 * Phase 1 rule: one email = one club. Owner self-creation (the founder
 * who personally signs up to run a 2nd club via /onboarding) bypasses
 * this check because it doesn't go through `inviteStaff` — that path
 * writes the owner's staff doc directly.
 */
export class EmailAlreadyInUseError extends Error {
  constructor() {
    super('Email already in use by another staff member')
    this.name = 'EmailAlreadyInUseError'
  }
}

export interface InviteStaffPayload {
  name: string
  email: string
  role: string
  invitedByUid: string
}

export class StaffRepository extends FirestoreRepository<typeof StaffMemberSchema> {
  constructor(clubId: string) {
    super(clubSubcollections.staff(clubId), StaffMemberSchema)
  }

  listAll(): Promise<StaffMember[]> {
    return this.list(orderBy('createdAt', 'asc'))
  }

  /**
   * Staff who have completed sign-up (i.e. `uid` is set). Filters client-side
   * to avoid the composite `uid != null + createdAt` index on the shared
   * backend; staff rolls per club are tiny.
   */
  async listJoined(): Promise<StaffMember[]> {
    const all = await this.listAll()
    return all.filter((s) => s.uid != null && s.uid !== '')
  }

  findByUid(uid: string): Promise<StaffMember[]> {
    return this.list(where('uid', '==', uid))
  }

  /**
   * Create a pending staff invite. `uid` stays null until the invitee accepts
   * their invite link and signs up — see the existing `accept-invite-page` +
   * `complete_staff_invite` Cloud Function. The token returned here goes in the
   * invite email's URL: `/accept-invite?invite_token=...`.
   *
   * Throws `EmailAlreadyInUseError` when the email is already on a staff
   * doc anywhere in the platform (any club, any state — pending, active,
   * or deactivated). Phase 1 rule: one email = one club. To free an email,
   * the owning club must fully `removeStaff`.
   *
   * Email is normalised (trim + lowercase) before storage and lookup so
   * mixed-case re-invites still collide.
   */
  async inviteStaff(input: InviteStaffPayload): Promise<{ id: string; token: string }> {
    const email = normalizeEmail(input.email)
    if (await emailIsTaken(email)) {
      throw new EmailAlreadyInUseError()
    }
    const clubId = this.path.split('/')[1] ?? ''
    const col = collection(db, this.path)
    const ref = doc(col)
    const token = crypto.randomUUID()
    await setDoc(ref, {
      clubId,
      name: normalizeName(input.name),
      email,
      role: input.role,
      isActive: true,
      uid: null,
      invitedAt: serverTimestamp(),
      joinedAt: null,
      invitedByUid: input.invitedByUid,
      avatarUrl: null,
      inviteToken: token,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return { id: ref.id, token }
  }

  /**
   * Patch a staff member's role, name, or isActive flag atomically.
   *
   * Reads the current doc inside a transaction so two admins changing the
   * same staff member's role concurrently don't silently overwrite each
   * other. If the caller provides `expected` (the state they saw when they
   * opened the edit drawer), the transaction compares and throws
   * `StaleStaffError` when the server copy has diverged — enabling the UI
   * to offer "reload and try again" instead of clobbering.
   *
   * When `expected` is omitted, last-writer-wins for backwards compatibility
   * (existing callers that don't need optimistic-concurrency).
   */
  async updateStaff(
    staffId: string,
    patch: Partial<StaffMember>,
    expected?: { role?: string; isActive?: boolean },
  ): Promise<void> {
    const ref = doc(db, this.path, staffId)
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new Error(`staff ${staffId} not found`)
      if (expected) {
        const data = snap.data() ?? {}
        if (expected.role !== undefined && data['role'] !== expected.role) {
          throw new StaleStaffError(expected.role, data['role'])
        }
        if (expected.isActive !== undefined && data['isActive'] !== expected.isActive) {
          throw new StaleStaffError(expected.isActive, data['isActive'])
        }
      }
      tx.update(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
      })
    })
  }

  /**
   * Remove a staff entry entirely. Prefer `updateStaff(id, { isActive: false })`
   * for history preservation; use this only when a mistaken invite needs to
   * disappear. Does not revoke the user's Firebase Auth account.
   */
  async removeStaff(staffId: string): Promise<void> {
    const ref = doc(db, this.path, staffId)
    await deleteDoc(ref)
  }

  /**
   * Re-issue the invite token for a pending staff record. Generates a fresh
   * UUID + bumps `invitedAt` so the existing `on_staff_created` Cloud Function
   * trigger fires and queues a new email — but the trigger only runs on doc
   * create, so we instead delete + recreate the doc atomically. Pending docs
   * (`uid == null`) only carry the invite shell, so the create-side trigger
   * handles the email path identically.
   *
   * Returns the new token so the UI can confirm "invitation re-enviada".
   * Throws if the staff record has already been claimed (`uid != null`).
   */
  async resendInvite(
    staffId: string,
    invitedByUid: string,
  ): Promise<{ id: string; token: string }> {
    const oldRef = doc(db, this.path, staffId)
    return runTransaction(db, async (tx) => {
      const snap = await tx.get(oldRef)
      if (!snap.exists()) throw new Error(`staff ${staffId} not found`)
      const data = snap.data() ?? {}
      if (data['uid'] != null && data['uid'] !== '') {
        throw new Error('Esta invitación ya fue aceptada.')
      }
      const token = crypto.randomUUID()
      const newRef = doc(collection(db, this.path))
      const clubId = this.path.split('/')[1] ?? ''
      tx.set(newRef, {
        clubId,
        name: data['name'] ?? '',
        email: data['email'] ?? '',
        role: data['role'] ?? 'receptionist',
        isActive: true,
        uid: null,
        invitedAt: serverTimestamp(),
        joinedAt: null,
        invitedByUid,
        avatarUrl: data['avatarUrl'] ?? null,
        inviteToken: token,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      tx.delete(oldRef)
      return { id: newRef.id, token }
    })
  }

  /**
   * Stamp the caller's own staff doc with `welcomeDialogSeenAt`. The
   * `memberId` MUST equal `request.auth.uid` — Firestore rules reject
   * any other path (see firestore.rules § Self-update). Best-effort:
   * a failure here just means the dialog will re-show next session,
   * which is acceptable.
   */
  async markWelcomeDialogSeen(memberId: string): Promise<void> {
    if (!memberId) return
    await updateDoc(this.docRef(memberId), {
      welcomeDialogSeenAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  }

  /**
   * Toggle push for ONE of the caller's own devices. Writes
   * `devices.{deviceId}.enabled` on the caller's staff doc — `memberId` MUST
   * equal `request.auth.uid` (Firestore rules reject any other path, see
   * § Self-update). The CF `_get_staff_fcm_tokens` skips entries where
   * `enabled === false`, so flipping this silences/restores push on that one
   * device (e.g. the always-open desktop) without touching others.
   */
  async setDeviceEnabled(
    memberId: string,
    deviceId: string,
    enabled: boolean,
  ): Promise<void> {
    if (!memberId || !deviceId) return
    await updateDoc(this.docRef(memberId), {
      [`devices.${deviceId}.enabled`]: enabled,
      updatedAt: serverTimestamp(),
    })
  }
}

/**
 * Cross-club uniqueness lookup for staff email. Calls a callable Cloud
 * Function — the client cannot do this query directly because the
 * `staff` collection-group rule restricts cross-club reads to "doc
 * email == my email OR doc uid == my uid" (see `firestore.rules`).
 * The CF uses the admin SDK to bypass rules, then enforces caller
 * authentication so anonymous browsers can't enumerate emails.
 *
 * Returns `true` if any staff doc carries this email anywhere
 * (pending, active, or deactivated).
 */
async function emailIsTaken(email: string): Promise<boolean> {
  if (!email) return false
  const fn = httpsCallable<{ email: string }, { taken: boolean }>(
    functions,
    'check_staff_email_available',
  )
  const res = await fn({ email })
  return res.data.taken === true
}
